import React, { useState, useEffect } from "react";
import { API_URL } from "../config/api";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import PlatformCard from "../components/PlatformCard";
import ReportsTab from "../components/ReportsTab";
import ServicesList from "../components/ServicesList";
import ReportsList from "../components/ReportsList";
import UpdatesTimeline from "../components/UpdatesTimeline";
import logo from "../assets/WhatsApp-Image-2026-05-03-at-17.16.41.jpeg";
import whatsappLogo from "../assets/whatsapp.png";
import seoLogo from "../assets/seo.png";
import LogOut from "../components/LogOut";
import { openPrint } from "../utils/print";
import { REPORT_TAB_LABELS } from "../constants/reportTabs";

const platformColors = {
  Instagram: "#E1306C",
  Facebook: "#1877F2",
  TikTok: "#000000",
  Snapchat: "#FFFC00",
  "Google Analytics": "#EA4335",
  WhatsApp: "#25D366",
  SEO: "#F5DEB3",
};

const Dashboard = () => {
  const [platforms, setPlatforms] = useState([]);
  const [activePlatforms, setActivePlatforms] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [lastUpdateDate, setLastUpdateDate] = useState(null);
  const [activeView, setActiveView] = useState("platforms"); // "platforms" | "reports" | "updates"
  const [services, setServices] = useState([]);
  const [reports, setReports] = useState({
    programming: [],
    design: [],
    other: [],
  });
  const [activeReportTab, setActiveReportTab] = useState("programming");

  // ─── Updates / Timeline ─────────────────────────────────────────────
  const [updates, setUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(true);

  const token = localStorage.getItem("token");
  const img = localStorage.getItem("image");
  const nameb = localStorage.getItem("name");

  // ─── Fetch user data ───────────────────────────────────────────────
  useEffect(() => {
    fetch("http://backend.goldensection.agency/public/api/myinfo", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const socialPlatforms = data.social_media.map((p) => ({
          id: p.id,
          name: p.platform,
          type: "social",
          logo: `/assets/${p.platform.toLowerCase().replace(" ", "")}.png`,
          color: platformColors[p.platform] || "#000000",
          analyses: p.analyses,
        }));

        const whatsappData = data.whatsapps || [];
        const whatsappPlatforms = whatsappData.length
          ? [
              {
                id: "whatsapp",
                name: "WhatsApp",
                type: "whatsapp",
                logo: whatsappLogo,
                color: platformColors["WhatsApp"],
                analyses: whatsappData,
              },
            ]
          : [];

        const seoData = data.seos || [];
        const seoPlatforms = seoData.length
          ? [
              {
                id: "seo",
                name: "SEO",
                type: "seo",
                logo: seoLogo,
                color: platformColors["SEO"],
                analyses: seoData,
              },
            ]
          : [];

        const allPlatforms = [
          ...socialPlatforms,
          ...whatsappPlatforms,
          ...seoPlatforms,
        ];
        setPlatforms(allPlatforms);
        const services = [
          {
            id: "media_buying",
            name: "خدمة الميديا باينج",
            platforms: allPlatforms.filter((p) =>
              [
                "Facebook",
                "Instagram",
                "TikTok",
                "Google Analytics",
                "Snapchat",
              ].includes(p.name),
            ),
          },
          {
            id: "whatsapp",
            name: "خدمة حملات الواتس اب",
            platforms: allPlatforms.filter((p) => p.type === "whatsapp"),
          },
          {
            id: "seo",
            name: "خدمة SEO",
            platforms: allPlatforms.filter((p) => p.type === "seo"),
          },
        ].filter((service) => service.platforms.length > 0);

        setServices(services);
        setActivePlatforms(allPlatforms.map((p) => p.id));

        const allDates = allPlatforms
          .flatMap((p) => p.analyses || [])
          .map((a) => a.updated_at || a.created_at)
          .filter(Boolean)
          .map((d) => new Date(d))
          .sort((a, b) => b - a);

        if (allDates.length > 0) setLastUpdateDate(allDates[0]);

        setUserInfo(
          seoData.length > 0
            ? {
                name: seoData[0].brand_name || "العميل",
                image:
                  `http://backend.goldensection.agency/public/api/${seoData[0].logo}` ||
                  logo,
              }
            : { name: "العميل", image: logo },
        );
      })
      .catch((err) => console.error(err));
  }, [token]);

  // ─── Fetch reports ─────────────────────────────────────────────────
  useEffect(() => {
    fetch("http://backend.goldensection.agency/public/api/myreports", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setReports(data);
      })
      .catch((err) => console.error(err));
  }, [token]);

  // ─── Fetch updates / timeline ───────────────────────────────────────
  // ملحوظة: الـ endpoint ده لسه مش جاهز من الباك اند - الكود جاهز ومستنى بس
  // الـ API يرجع array من العناصر، أو { updates: [...] } - الاتنين مدعومين
  useEffect(() => {
    setUpdatesLoading(true);
    fetch("http://backend.goldensection.agency/public/api/myupdates", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data.updates || [];
        setUpdates(list);
      })
      .catch((err) => console.error(err))
      .finally(() => setUpdatesLoading(false));
  }, [token]);

  // ─── Helpers ───────────────────────────────────────────────────────
  const handleSelect = (id) =>
    setActivePlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );

  const toggleAll = () =>
    setActivePlatforms(
      activePlatforms.length === platforms.length
        ? []
        : platforms.map((p) => p.id),
    );

  const filterAnalysesByDate = (analyses) => {
    if (!dateFrom && !dateTo) return analyses;
    return analyses.filter((a) => {
      const d = new Date(a.updated_at || a.created_at);
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;
      if (to) to.setHours(23, 59, 59, 999);
      return (from ? d >= from : true) && (to ? d <= to : true);
    });
  };

  const filteredPlatforms = platforms.map((p) => ({
    ...p,
    analyses: filterAnalysesByDate(p.analyses || []),
  }));

  const handleDateChange = (type, value) =>
    type === "from" ? setDateFrom(value) : setDateTo(value);

  // ─── Print (موبايل) - نفس اللي في Sidebar.jsx بس لمحتوى الموبايل ────
  const printPlatformCards = () => {
    const content = document.querySelector(".platform-cards")?.innerHTML || "";
    openPrint(
      `تقرير متابعة أرقام ${userInfo?.name}`,
      `<div class="platform-content">${content}</div>`,
      nameb,
    );
  };

  const printReports = () => {
    const content = document.querySelector(".reports-content")?.innerHTML || "";
    const label = REPORT_TAB_LABELS[activeReportTab] || "التقارير";
    openPrint(
      `تقرير ${label}`,
      `<h3 style="text-align:center;margin-bottom:20px">تقرير ${label}</h3><div>${content}</div>`,
      nameb,
    );
  };

  const printUpdates = () => {
    const content = document.querySelector(".updates-content")?.innerHTML || "";
    openPrint(
      `آخر التحديثات - ${nameb}`,
      `<h3 style="text-align:center;margin-bottom:20px">آخر التحديثات</h3><div>${content}</div>`,
      nameb,
    );
  };

  // ─── Shared props for Sidebar (desktop) ────────────────────────────
  const sidebarProps = {
    platforms,
    services,
    onSelect: handleSelect,
    activePlatforms,
    toggleAll,
    darkMode,
    userInfo,
    dateFrom,
    dateTo,
    onDateChange: handleDateChange,
    lastUpdateDate,
    activeView,
    setActiveView,
    activeReportTab,
    setActiveReportTab,
    reports,
  };

  // ─── Shared content renderer (desktop + mobile) ─────────────────────
  const renderContent = () => (
    <>
      {activeView === "platforms" && (
        <div className="platform-cards">
          {filteredPlatforms
            .filter((p) => activePlatforms.includes(p.id))
            .map((platform) => (
              <PlatformCard key={platform.id} platform={platform} active />
            ))}
        </div>
      )}
      {activeView === "reports" && (
        <div className="reports-content">
          <ReportsTab
            reports={reports}
            activeReportTab={activeReportTab}
            setActiveReportTab={setActiveReportTab}
          />
        </div>
      )}
      {activeView === "updates" && (
        <UpdatesTimeline updates={updates} loading={updatesLoading} />
      )}
    </>
  );

  return (
    <>
      {/* ── Desktop ── */}
      <div className="dashboard">
        <Sidebar {...sidebarProps} />
        <div className="all_dashboard">
          <Navbar
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            userInfo={userInfo}
            platforms={platforms}
            onSelect={handleSelect}
            activePlatforms={activePlatforms}
            toggleAll={toggleAll}
          />
          <div className="dashboard-content">{renderContent()}</div>
        </div>
      </div>

      {/* ── Mobile ── */}
      <div className="mobiledash">
        {/* Navbar mobile */}
        <div className="navbarmobile">
          <div className="nav-left">
            <img
              src={`http://backend.goldensection.agency/public/${img}`}
              alt={img}
              className="nav-avatar"
            />
            <h1 className="nav-username">مرحبا {nameb}</h1>
          </div>
          <div className="nav-right" onClick={() => setOpen(true)}>
            <span />
            <span />
            <span />
          </div>
        </div>

        {/* Sidebar mobile */}
        <div
          className={`mobile-sidebar ${open ? "active" : ""}`}
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "none",
          }}
        >
          <button className="close-btn" onClick={() => setOpen(false)}>
            ×
          </button>

          <div className="navlogo2">
            <h3>Golden Section</h3>
            <img id="logo2" src={logo} alt="" />
          </div>

          {/* آخر تحديث */}
          {activeView === "platforms" && lastUpdateDate && (
            <div className="last-update-box">
              <span className="last-update-icon">🕐</span>
              <div className="last-update-text">
                <span className="last-update-label">آخر تحديث للبيانات</span>
                <span className="last-update-val">
                  {new Date(lastUpdateDate).toLocaleDateString("ar-EG", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>
          )}

          {activeView === "reports" && reports?.last_update?.date && (
            <div className="last-update-box">
              <span className="last-update-icon">🕐</span>
              <div className="last-update-text">
                <span className="last-update-label">آخر تحديث للتقارير</span>
                <span className="last-update-val">
                  {new Date(reports.last_update.date).toLocaleDateString(
                    "ar-EG",
                  )}
                </span>
              </div>
            </div>
          )}

          {/* مفتاح التبديل */}
          <div className="view-switch">
            <button
              className={`view-switch-btn ${activeView === "platforms" ? "active" : ""}`}
              onClick={() => {
                setActiveView("platforms");
                setOpen(false);
              }}
            >
              لوحة البيانات
            </button>
            <button
              className={`view-switch-btn ${activeView === "reports" ? "active" : ""}`}
              onClick={() => {
                setActiveView("reports");
                setOpen(false);
              }}
            >
              التقارير
            </button>
            <button
              className={`view-switch-btn ${activeView === "updates" ? "active" : ""}`}
              onClick={() => {
                setActiveView("updates");
                setOpen(false);
              }}
            >
              آخر التحديثات
            </button>
          </div>

          {/* قائمة المنصات */}
          {activeView === "platforms" && (
            <>
              <ServicesList
                services={services}
                platforms={platforms}
                activePlatforms={activePlatforms}
                onSelect={handleSelect}
                toggleAll={toggleAll}
              />
              <button className="print" onClick={printPlatformCards}>
                🖨️ طباعة التقرير
              </button>
            </>
          )}

          {/* قائمة التقارير */}
          {activeView === "reports" && (
            <>
              <h3 style={{ marginTop: "10px" }}>اختر نوع التقرير</h3>
              <ReportsList
                reports={reports}
                activeReportTab={activeReportTab}
                setActiveReportTab={setActiveReportTab}
              />
              {activeReportTab !== "comreport" && (
                <button className="print" onClick={printReports}>
                  🖨️ طباعة {REPORT_TAB_LABELS[activeReportTab] || "التقارير"}
                </button>
              )}
            </>
          )}

          {/* آخر التحديثات */}
          {activeView === "updates" && (
            <>
              <h3 style={{ marginTop: "10px" }}>آخر التحديثات</h3>
              <p className="empty-service">
                آخر تحديث حصل في أي تقرير أو منصة، مع التاريخ ومكان التحديث.
              </p>
              <button className="print" onClick={printUpdates}>
                🖨️ طباعة آخر التحديثات
              </button>
            </>
          )}

          <div className="out">
            <LogOut />
          </div>
        </div>

        {/* المحتوى mobile */}
        <div className="platform-cardsmobile">{renderContent()}</div>
      </div>
    </>
  );
};

export default Dashboard;
